import React, { useCallback, useMemo, useState } from 'react';
import { camelCase, isNil } from 'lodash';

import {
  BASE_YEAR_VALUE_TYPE_OPTIONS,
  DOLLAR_AMOUNT_TYPE_OPTIONS,
  OTHER_REIMBURSEMENT_TYPE_OPTIONS,
  REIMBURSEMENT_TYPES,
  TAX_REIMBURSEMENT_TYPE_OPTIONS,
} from '../constants';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  FormControl,
  DialogActions,
} from '@mui/material';
import {
  CommercialReimbursement,
  DollarAmountReimbursement,
  IncreaseOverBaseYearReimbursement,
  PercentOfForecastReimbursement,
  PercentOfCurrentYearLiabilityReimbursement,
  BaseYearValueType,
  DollarAmountType,
  CommercialReimbursementBase,
  ReimbursementType,
} from 'types/underwriting/income-approach';
import { DEFAULT_EXPENSES, DEFAULT_EXPENSES_KEYS } from 'shared/constants/expenses';
import { useRootStore } from 'stores/RootStore';
import { expenseForecastFormHelpers } from 'components/underwriting/income-approach/expenses/expense-forecast/expenseForecastFormConfig';
import { INCOME_APPROACH_BASIS_TYPES, incomeApproachCalculations } from 'shared/calculations/incomeApproach';
import { UnderwritingOutput } from 'types/underwriting/underwriting';
import { Field } from 'mobx-react-form';

type CommercialReimbursementSettings = Partial<
  Pick<CommercialReimbursement, 'expenseCategory' | 'reimbursementType'>
> & {
  dollarAmountType?: DollarAmountType;
  adminFeeKnown?: boolean;
  baseYearValueType?: BaseYearValueType;
};

type ReimbursementEditModalProps = {
  reimbursementField?: Field;
  commercialVCLoss?: number;
  onCancel: () => void;
  onSave: (reimbursement: CommercialReimbursement) => void;
  isCreateModal: boolean;
};

const ReimbursementEditModal: React.FC<ReimbursementEditModalProps> = ({
  reimbursementField,
  onCancel,
  onSave,
  isCreateModal = false,
}) => {
  const reimbursement: CommercialReimbursement = reimbursementField?.value || ({} as CommercialReimbursement);
  const { underwritingPageStore } = useRootStore();
  const { underwritingData = {} as UnderwritingOutput } = underwritingPageStore;
  const residentialUnits = underwritingData?.property.residentialUnits || 0;
  const grossBuildingArea = underwritingData?.property.grossBuildingArea || 0;
  const rentRoll = underwritingData?.incomeApproach?.commercial?.rentRoll || [];
  const expenseForecasts = underwritingData?.incomeApproach?.expenses?.expenseForecasts || [];
  const currentYearTaxLiability = underwritingData?.incomeApproach?.taxes?.taxLiability || 0;
  const commercialVCLoss = underwritingData?.incomeApproach?.commercial?.vcLossPercentage || 0;

  const effectiveGrossIncome = incomeApproachCalculations.calculateEffectiveGrossIncome(
    underwritingData,
    INCOME_APPROACH_BASIS_TYPES.borrower
  );

  const [currentReimbursementSettings, setCurrentReimbursementSettings] = useState<CommercialReimbursementSettings>({
    expenseCategory: reimbursement.expenseCategory,
    reimbursementType: reimbursement.reimbursementType,
    dollarAmountType: (reimbursement as DollarAmountReimbursement)?.dollarAmountType,
    adminFeeKnown:
      (reimbursement as PercentOfForecastReimbursement)?.adminFeeKnown ||
      (reimbursement as PercentOfCurrentYearLiabilityReimbursement)?.adminFeeKnown ||
      false,
    baseYearValueType: (reimbursement as IncreaseOverBaseYearReimbursement)?.baseYearValueType,
  });

  const expenseCategories = useMemo(() => {
    return underwritingData?.incomeApproach?.expenses?.expenseCategories || [];
  }, [underwritingData?.incomeApproach?.expenses?.expenseCategories]);
  const reimbursementExpenseCategories = useMemo(() => {
    if (expenseCategories && expenseCategories.length) {
      return expenseCategories;
    }
    return DEFAULT_EXPENSES;
  }, [expenseCategories]);
  const isValid = useMemo(() => {
    if (!currentReimbursementSettings.expenseCategory || !currentReimbursementSettings.reimbursementType) {
      return false;
    }

    if (
      currentReimbursementSettings.reimbursementType === REIMBURSEMENT_TYPES.dollarAmount.value &&
      isNil(currentReimbursementSettings.dollarAmountType)
    ) {
      return false;
    }
    if (
      currentReimbursementSettings.reimbursementType === REIMBURSEMENT_TYPES.increaseOverBaseYear.value &&
      isNil(currentReimbursementSettings.baseYearValueType)
    ) {
      return false;
    }
    return true;
  }, [currentReimbursementSettings]);

  const onSubmit = () => {
    const expenseTypeLabel = reimbursementExpenseCategories.find(
      (category) => category.key === currentReimbursementSettings.expenseCategory
    )?.category;
    const reimbursementTypeLabel = REIMBURSEMENT_TYPES[currentReimbursementSettings.reimbursementType!].label;

    const name = `${expenseTypeLabel} (${reimbursementTypeLabel})`;
    const key = `${camelCase(currentReimbursementSettings.expenseCategory)}_${
      currentReimbursementSettings.reimbursementType
    }`;

    const reimbursementToSave: CommercialReimbursementBase = {
      expenseCategory: currentReimbursementSettings.expenseCategory!,
      reimbursementType: currentReimbursementSettings.reimbursementType!,
      vcLoss: commercialVCLoss,
      name,
      key,
    };

    if (currentReimbursementSettings.reimbursementType === 'dollarAmount') {
      (reimbursementToSave as DollarAmountReimbursement).dollarAmountType =
        currentReimbursementSettings.dollarAmountType!;
      (reimbursementToSave as DollarAmountReimbursement).unitReimbursements = rentRoll.map((unit) => ({
        unitId: unit.id,
        tenant: unit.tenant,
        monthly: 0,
        annual: 0,
        enabled: true,
      }));
      if (reimbursementField) {
        if (reimbursementField.has('adminFeeKnown')) {
          reimbursementField.del('adminFeeKnown');
        }
        reimbursementField.del('unitReimbursements');
      }
    }
    if (currentReimbursementSettings.reimbursementType === 'percentOfCurrentYearLiability') {
      (reimbursementToSave as PercentOfCurrentYearLiabilityReimbursement).adminFeeKnown =
        currentReimbursementSettings.adminFeeKnown!;
      (reimbursementToSave as PercentOfCurrentYearLiabilityReimbursement).unitReimbursements = rentRoll.map((unit) => ({
        unitId: unit.id,
        tenant: unit.tenant,
        currentYearLiability: currentYearTaxLiability,
        percentOfCurrentYearLiability: 0,
        annualLessAdminFee: 0,
        adminFeePercentage: 0,
        grossAdminFee: 0,
        annual: 0,
        enabled: true,
      }));
      if (reimbursementField) {
        if (reimbursementField.has('dollarAmountType')) {
          reimbursementField.del('dollarAmountType');
        }
        reimbursementField.del('unitReimbursements');
      }
    }
    if (currentReimbursementSettings.reimbursementType === 'percentOfForecast') {
      (reimbursementToSave as PercentOfForecastReimbursement).adminFeeKnown =
        currentReimbursementSettings.adminFeeKnown!;
      const expenseForecast = expenseForecasts?.find(
        (expenseForecast) => expenseForecast.key === currentReimbursementSettings.expenseCategory
      );
      const grossAppraiserForecast = expenseForecastFormHelpers.getConcludedValueByBasis(
        expenseForecast?.concludedValue || 0,
        expenseForecast?.basis || 'psf',
        grossBuildingArea,
        residentialUnits,
        effectiveGrossIncome
      );
      (reimbursementToSave as PercentOfForecastReimbursement).unitReimbursements = rentRoll.map((unit) => ({
        unitId: unit.id,
        tenant: unit.tenant,
        annual: 0,
        enabled: true,
        grossForecastedValue: grossAppraiserForecast.total,
        percentOfGrossForecastedValue: 0,
        annualLessAdminFee: 0,
        adminFeePercentage: 0,
        grossAdminFee: 0,
      }));
      if (reimbursementField) {
        if (reimbursementField.has('dollarAmountType')) {
          reimbursementField.del('dollarAmountType');
        }
        reimbursementField.del('unitReimbursements');
      }
    }
    // if (currentReimbursementSettings.reimbursementType === 'increaseOverBaseYear') {
    //   (reimbursementToSave as IncreaseOverBaseYearReimbursement).baseYearValueType =
    //     currentReimbursementSettings.baseYearValueType!;
    //   (reimbursementToSave as IncreaseOverBaseYearReimbursement).unitReimbursements = rentRoll.map((unit) => ({
    //     unitId: unit.id,
    //     tenant: unit.tenant,
    //     annual: 0,
    //     enabled: true,
    //     baseYear: '',
    //     baseYearTaxRate: 0,
    //     baseYearAssessment: 0,
    //     baseYearLiability: 0,
    //     currentYearLiability: 0,
    //     increase: 0,
    //     share: 0,
    //   }));
    // }

    onSave({
      ...reimbursementToSave,
      grossVCLoss: 0,
      grossAnnualValue: 0,
      effectiveAnnualValue: 0,
    } as CommercialReimbursement);
  };

  const onChange = useCallback(
    (updates: CommercialReimbursementSettings) => {
      setCurrentReimbursementSettings({ ...currentReimbursementSettings, ...updates });
    },
    [currentReimbursementSettings, setCurrentReimbursementSettings]
  );

  const renderKnownInformationSection = () => {
    const { reimbursementType, dollarAmountType, baseYearValueType, adminFeeKnown } = currentReimbursementSettings;

    switch (reimbursementType) {
      case REIMBURSEMENT_TYPES.dollarAmount.value:
        return (
          <Grid container>
            <Grid item xs={12}>
              <InputLabel shrink>Known Information</InputLabel>
            </Grid>
            <Grid item xs={12}>
              <RadioGroup
                row
                value={dollarAmountType}
                onChange={(e) => onChange({ dollarAmountType: e.target.value as DollarAmountType })}
              >
                {DOLLAR_AMOUNT_TYPE_OPTIONS.map((type) => (
                  <FormControlLabel key={type.value} value={type.value} control={<Radio />} label={type.label} />
                ))}
              </RadioGroup>
            </Grid>
          </Grid>
        );

      case REIMBURSEMENT_TYPES.increaseOverBaseYear.value:
        return (
          <Grid container>
            <Grid item xs={12}>
              <InputLabel shrink>Known Information</InputLabel>
            </Grid>
            <Grid item xs={12}>
              <RadioGroup
                row
                value={baseYearValueType}
                onChange={(e, value) => onChange({ baseYearValueType: value as BaseYearValueType })}
              >
                {BASE_YEAR_VALUE_TYPE_OPTIONS.map((type) => (
                  <FormControlLabel key={type.value} value={type.value} control={<Radio />} label={type.label} />
                ))}
              </RadioGroup>
            </Grid>
          </Grid>
        );

      case REIMBURSEMENT_TYPES.percentOfCurrentYearLiability.value:
      case REIMBURSEMENT_TYPES.percentOfForecast.value:
        return (
          <Grid container>
            <Grid item xs={12}>
              <InputLabel shrink>Known Information</InputLabel>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    value={adminFeeKnown}
                    checked={adminFeeKnown}
                    onChange={(e, value) => onChange({ adminFeeKnown: value })}
                  />
                }
                label="Admin Fee"
              />
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  const expenseCategoriesOptions = useMemo(() => {
    return reimbursementExpenseCategories.map((expenseCategory) => ({
      value: expenseCategory.key,
      label: expenseCategory.category,
    }));
  }, [reimbursementExpenseCategories]);

  return (
    <Dialog open maxWidth="md" onClose={onCancel}>
      <DialogTitle id="alert-dialog-title">{isCreateModal ? 'Add' : 'Edit'} Reimbursement</DialogTitle>
      <DialogContent sx={{ width: 700, padding: '24px' }}>
        <Grid container justifyContent="flex-start" alignItems="center">
          <Grid item xs={12} style={{ marginRight: 16 }}>
            <FormControl fullWidth sx={{ marginTop: 2, marginBottom: 2 }}>
              <InputLabel>Expense Category</InputLabel>
              <Select
                label="Expense Category"
                fullWidth
                value={currentReimbursementSettings.expenseCategory}
                onChange={(e) => {
                  onChange({ expenseCategory: e.target.value as string });
                }}
              >
                {expenseCategoriesOptions.map(({ value, label }) => (
                  <MenuItem key={value} value={value}>
                    {label || value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        {currentReimbursementSettings.expenseCategory && (
          <Grid container>
            <Grid item xs={12}>
              <InputLabel shrink>Reimbursement Type</InputLabel>
            </Grid>
            <Grid item xs={12}>
              <RadioGroup
                row
                value={currentReimbursementSettings.reimbursementType}
                onChange={(e) => onChange({ reimbursementType: e.target.value as ReimbursementType })}
              >
                {(currentReimbursementSettings.expenseCategory === DEFAULT_EXPENSES_KEYS.realEstateTaxes
                  ? TAX_REIMBURSEMENT_TYPE_OPTIONS
                  : OTHER_REIMBURSEMENT_TYPE_OPTIONS
                ).map((type) => (
                  <FormControlLabel key={type.value} value={type.value} control={<Radio />} label={type.label} />
                ))}
              </RadioGroup>
            </Grid>
          </Grid>
        )}
        {renderKnownInformationSection()}
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" autoFocus disabled={!isValid} onClick={onSubmit}>
          {isCreateModal ? 'Add' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReimbursementEditModal;
